import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FileItem } from '@models/file-item.model';
import { Invoice, InvoiceLineItem } from '@models/invoice.model';
import { PurchaseOrderDto } from '@app/models/purchase-order.model.dto';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { InvoiceService } from '@services/invoice.service';
import { Toaster } from '@services/toaster.service';
import { LocalStorage } from 'ngx-webstorage';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import CONFIG from '@app/app.config';
import { environment } from '@env/environment';


@Component({
  selector: 'ngx-add-po-invoice',
  templateUrl: './purchase-order-invoice-form.component.html',
  styleUrls: ['./purchase-order-invoice-form.component.scss'],
})
export class PurchaseOrderInvoiceFormComponent implements OnInit, OnDestroy {
  isRCDSS: boolean = environment?.name === 'rcdss';
  invoiceForm: FormGroup;
  invoiceLineItemForm: FormGroup;
  availableLineItems: any[] = [];
  selectedLineItems: InvoiceLineItem[] = [];
  lineItemsFiltered$: Observable<any>;
  lineItems$ = new BehaviorSubject('');
  @Input() purchaseOrder: PurchaseOrderDto;
  @Input() invoices: Invoice[] = [];
  @Input() invoice: Invoice;

  @LocalStorage() organizationId;

  files: (File | FileItem)[] = [];
  deletedSavedLineItems: any[] = [];
  fileChanged = false;

  constructor(
    private ngbActiveModal: NgbActiveModal,
    private invoiceService: InvoiceService,
    private fb: FormBuilder,
    private $toaster: Toaster,
  ) {
  }

  ngOnInit() {
    this.invoiceForm = this.fb.group({
      description: [],
    });

    this.invoiceLineItemForm = this.fb.group({
      line_item_id: [null, [Validators.required]],
      quantity_invoiced: [null, [Validators.required, Validators.min(1)]],
    });

    this.lineItemsFiltered$ = this.lineItems$.pipe(
      switchMap((term: string) => {
        const regex = new RegExp(term, 'i');
        const availableItems = this.availableLineItems
          .filter(
            (item) => !this.selectedLineItems
              .find((selectedItem) => item.id === selectedItem.line_item_id),
          ).filter(item => regex.test(item.product_name));
        return of(availableItems);
      }),
    );

    this.availableLineItems = this.purchaseOrder.line_items;
    if (this.invoice && this.invoice.id) {
      this.selectedLineItems = (this.invoice.line_items as any) || [];
      this.invoiceForm.patchValue(this.invoice);
    }
    if (this.invoice) {
      this.files = [this.invoice.file];
    }
  }

  saveInvoice() {
    if (this.files.length && this.invoiceForm.valid && this.verifyQuantities()) {
      const invoiceFormData = new Invoice(this.invoiceForm.getRawValue());
      invoiceFormData.line_items = this.selectedLineItems;
      let request;

      if (this.invoice && this.invoice.id) {
        request = this.deleteSavedLineItems()
          .pipe(switchMap(() => {
              return this.invoiceService.updateInvoice(
                this.organizationId, this.purchaseOrder.id, this.invoice.id, invoiceFormData,
              );
            }),
            switchMap(() => {
              return this.updateInvoiceFiles();
            }),
          );
      } else {
        request = this.uploadInvoiceFiles()
          .pipe(switchMap((invoice: Invoice) => {
              return this.invoiceService.updateInvoice(
                this.organizationId, this.purchaseOrder.id, invoice.id, invoiceFormData,
              );
            }),
          );
      }

      request.subscribe((result) => {
        if (this.invoice && this.invoice.id) {
          this.$toaster.show('success', 'Success', 'Invoice has been updated');
          this.fileChanged = false;
        } else {
          this.$toaster.show('success', 'Success', 'Invoice has been added');
        }
        this.files = [];
        this.ngbActiveModal.close(result);
      }, (e) => {
        const errMsg = !!e.error && !!e.error.detail ? e.error.detail : e.message;
        this.$toaster.show('error', 'Error adding the invoice', errMsg);
      });
    }
  }

  removeInvoiceLineItem(lineItem, index) {
    const savedLineItem = this.selectedLineItems.splice(index, 1);
    if (lineItem.id) {
      this.deletedSavedLineItems.push(...savedLineItem);
    }

    this.lineItems$.next('');
    if (!this.selectedLineItems.length) {
      this.invoiceLineItemForm.get('line_item_id').setValidators([Validators.required]);
      this.invoiceLineItemForm.get('quantity_invoiced').setValidators([Validators.required, Validators.min(1)]);
    }
  }

  deleteSavedLineItems() {
    if (this.deletedSavedLineItems.length) {
      const deleteRequests = this.deletedSavedLineItems.map(li => {
        return this.invoiceService.deleteInvoiceLineItem(this.organizationId, this.purchaseOrder.id, this.invoice.id, li.id);
      });
      return forkJoin(deleteRequests);
    }
    return of([]);
  }

  addInvoiceLineItem() {
    if (
      this.invoiceLineItemForm.valid &&
      (!!this.invoiceLineItemForm.get('line_item_id').value &&
        !!this.invoiceLineItemForm.get('quantity_invoiced').value)
    ) {
      const lineItemValue = this.invoiceLineItemForm.getRawValue();
      const lineItem = this.availableLineItems.find(i => i.id === lineItemValue.line_item_id);
      this.selectedLineItems.push({
        ...lineItem,
        ...lineItemValue,
      });

      this.invoiceLineItemForm.reset();
      this.lineItems$.next('');
    }
  }

  closeModal() {
    this.ngbActiveModal.dismiss();
  }

  ngOnDestroy(): void {
    this.lineItems$.unsubscribe();
  }

  addFile(event: any) {
    const fileList: FileList = event.srcElement.files;

    if (fileList.length === 0) {
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (file.size > CONFIG.FILE_LIMIT) {
        this.$toaster.show('warning', 'File size', 'Maximum file size for uploads is 10 mb');
        continue;
      }
      this.files = [file];
      this.fileChanged = true;
    }

  }

  removeFile(file, $index) {
    this.files.splice($index, 1);
  }

  uploadInvoiceFiles() {
    const file: any = this.files[0];
    return this.invoiceService.attachInvoiceFile(this.organizationId, this.purchaseOrder.id, file);
  }

  updateInvoiceFiles() {
    if (!this.fileChanged || this.files.length === 0) {
      return of({});
    }
    const file: any = this.files[0];
    return this.invoiceService.updateInvoiceFile(this.organizationId, this.purchaseOrder.id, this.invoice.id, file);
  }

  verifyQuantities(): boolean {
    // TODO: this method should not display the toaster error message.
    const invoices = this.invoices || [];
    const editingInvoice = this.invoice || null;

    for (const item of this.selectedLineItems) {
      const max = Math.floor(item.quantity_ordered * 1.05);
      const totalInvoiced = invoices.reduce((acc, invoice) => {
        // Skip accumulating the invoice that's currently being edited.
        if (editingInvoice && editingInvoice.id === invoice.id)
          return acc;

        const invoiceItems = (invoice.line_items || []).filter(x => x.line_item_id === item.line_item_id);
        const sum = invoiceItems.reduce((x, i) => x + i.quantity_invoiced, 0);

        return acc + sum;
      }, 0);

      if ((totalInvoiced + item.quantity_invoiced) > max) {
        this.$toaster.show('error', `Max Invoice Quantity reached!`, `The total quantity invoiced cannot be greater than 105% of the Quantity Ordered for line item ${item.product_name}`);
        return false;
      }
    }

    return true;
  }
}
